import React from 'react';

// components
import { Link } from 'src/components/Inputs';
import { EmbeddedForeignContentBanner } from 'src/components/Feedback';

// helpers
import { useUserLocale } from 'src/hooks';
import { Locales } from 'src/lib/constants';

const renderForeignContentBanner = (localeCode) => {
  switch (localeCode) {
    case 'en-gb':
      return (
        <EmbeddedForeignContentBanner
          contentLocale={Locales.EN_US}
          href='#end-user-services-agreement-uk'
        />
      );
    case 'en-eu':
      return (
        <EmbeddedForeignContentBanner
          contentLocale={Locales.EN_US}
          href='#end-user-services-agreement-eea'
        />
      );
    default:
      return null;
  }
};

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const USEndUserServicesAgreement = () => {
  const { locale: userLocale } = useUserLocale();
  return (
    <>
      <h4
        id='end-user-services-agreement-us'
        data-magellan-target='end-user-services-agreement-us'
      >
        End User Services Agreement (US)
      </h4>
      {renderForeignContentBanner(userLocale?.localeCode)}
      <p>Effective Date: July 1, 2019</p>
      <p>
        Thanks for using Plaid! Plaid provides a platform (“
        <strong>Platform</strong>”) that allows you and other end users to
        connect your bank accounts and other financial accounts (“
        <strong>Accounts</strong>”) with applications that can help you do
        things like save for retirement, manage your spending, streamline credit
        applications or transfer money (“
        <strong>Apps</strong>”).
      </p>
      <p>
        This End User Services Agreement (US) (“
        <strong>Agreement</strong>”) is an agreement between you and Plaid Inc.
        (“
        <strong>Plaid</strong>”, “<strong>we</strong>” or “<strong>us</strong>
        ”). By accepting this Agreement or accessing my.plaid.com or your Plaid
        profile, you agree to this Agreement and certify that you have all
        necessary rights to do so. If you are accepting on behalf of your
        employer or another entity, you represent and warrant that: (i) you have
        full legal authority to bind your employer or such entity to this
        Agreement; (ii) you have read and understand this Agreement; and (iii)
        you agree to this Agreement on behalf of the party that you represent.
        If you do not have the legal authority to bind your employer or the
        applicable entity, please do not accept this Agreement or access the
        features covered by this Agreement.
      </p>
      <p>
        Please note that this Agreement does not apply to the services we
        provide to the developers of Apps. Those services are covered by our
        Developer Terms of Use and other terms.
      </p>
      <p>
        <strong>my.plaid.com</strong>.
      </p>
      <p>
        You can use my.plaid.com to manage connections between your Accounts and
        Apps. my.plaid.com is designed to empower you with greater control over
        your financial data, but the decision to use any App remains yours. Your
        use of any App and Account, and the App and Account providers’ use of
        your data, is governed by separate terms between you and the applicable
        providers. Plaid is not responsible for any Apps or Accounts provided by
        third parties or the acts or omissions of any third-party providers, and
        does not guarantee that any Apps or Accounts will remain available or
        compatible with the Platform.
      </p>
      <p>
        <strong>Plaid Profile Creation</strong>.
      </p>
      <p>
        You need to create an profile with Plaid in order to use certain
        features of the Platform, including certain features of my.plaid.com.
        You also need to ensure that your information is accurate, complete and
        up-to-date. You must notify us if you learn of any unauthorized access
        to or use of your Plaid Profile.
      </p>
      <p>
        <strong>Control and Responsibilities</strong>.
      </p>
      <p>
        You represent and warrant that you have all necessary rights to use your
        Accounts and Apps with the Platform, and you agree to comply with all
        laws and regulations applicable to your use, as well as any rules and
        guidelines that we post. You must not (1) use or access anyone else’s
        Accounts or related data, (2) submit information about anyone else’s
        identity or Accounts or that violates any third-party rights or (3) use
        the Platform for any fraudulent, illegal or misleading purpose. You also
        agree not to (a) modify, reverse engineer or seek to gain unauthorized
        access to the Platform or related systems, data or source code, (b)
        bypass or circumvent measures designed to prevent or limit access to any
        part of the Platform, (c) rent, lease, provide access to or sublicense
        any elements of the Platform to a third party or use the Platform on
        behalf of or to provide services to third parties, (d) copy, modify or
        create derivative works of the Platform or remove any of Plaid’s
        proprietary notices, (e) access the Platform for competitive purposes or
        publish any benchmark or performance information about the Platform, or
        (f) use the Platform in any manner that could damage, disable,
        overburden, or impair the functioning of the Platform or interfere with,
        disrupt or negatively affect other users.
      </p>
      <p>
        <strong>How Plaid Uses Your Data</strong>.
      </p>
      <p>
        Please review our{' '}
        <Link href='#end-user-privacy-policy'>
          <a>End User Privacy Policy</a>
        </Link>{' '}
        to learn how Plaid uses data related to your Accounts (e.g., your
        Account login information and balance information). You should also
        refer to our{' '}
        <Link href='#privacy-statement'>
          <a>Privacy Statement</a>
        </Link>{' '}
        and{' '}
        <Link href='#cookie-policy'>
          <a>Cookie Policy</a>
        </Link>{' '}
        for information about what we collect from the use of our websites. If
        you have questions, contact us at{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>
        .
      </p>
      <p>
        <strong>Rights to the Platform</strong>.
      </p>
      <p>
        Note that Plaid owns all right, title and interest (including
        intellectual property rights) in and to the Platform (including
        my.plaid.com and Plaid Profile features) and our related websites and
        technology. If you choose to give us feedback, suggestions or other
        inputs about the Platform, we may use them without restriction.
      </p>
      <p>
        <strong>Our Disclaimers</strong>.
      </p>
      <p>
        TO THE EXTENT PERMITTED BY LAW, THE PLATFORM (INCLUDING MY.PLAID.COM AND
        PLAID PROFILES) IS PROVIDED “AS IS” AND “AS AVAILABLE,” AND ANY USE IS
        AT YOUR DISCRETION AND RISK. PLAID, ITS AFFILIATES AND ITS AND THEIR
        SUPPLIERS MAKE NO WARRANTIES, EXPRESS OR IMPLIED, STATUTORY OR
        OTHERWISE, INCLUDING WARRANTIES OF MERCHANTABILITY, TITLE, FITNESS FOR A
        PARTICULAR PURPOSE OR NONINFRINGEMENT. PLAID DOES NOT WARRANT THAT USE
        WILL BE UNINTERRUPTED OR ERROR-FREE, THAT ANY OF YOUR DATA WILL BE
        ACCURATE OR COMPLETE OR THAT PLAID WILL MAINTAIN ANY DATA WITHOUT LOSS.
      </p>
      <p>
        <em>Liabilities for our Platform.</em> TO THE EXTENT PERMITTED BY LAW,
        PLAID, ITS AFFILIATES AND ITS AND THEIR SUPPLIERS WILL NOT BE
        RESPONSIBLE FOR: (A) ANY LOST PROFITS, LOSS OF USE, LOST OR INACCURATE
        DATA, FAILURE OF SECURITY MECHANISMS, FINANCIAL LOSSES, OR ANY INDIRECT,
        SPECIAL, INCIDENTAL, RELIANCE OR CONSEQUENTIAL DAMAGES OF ANY KIND OR
        (B) ANY DAMAGES OR AMOUNTS EXCEEDING, IN THE AGGREGATE, THE GREATER OF
        (1) THE AMOUNT YOU PAID US TO USE THE PLATFORM AND (2) ONE HUNDRED U.S.
        DOLLARS (US $100).
      </p>
      <p>
        <strong>Dispute Resolution.</strong>
      </p>
      <p>
        <strong>
          We hope you will have a positive experience using our Platform, but
          should a dispute between us arise out of or relating to these Terms,
          we agree to resolve the dispute by following these steps:
        </strong>
      </p>
      <ol>
        <li>
          <strong>
            Send us a notice, according to the Notices section below, describing
            the dispute and including all relevant facts so we know how to help
            you.
          </strong>
        </li>
        <li>
          <strong>
            Within 5 business days after our receipt of your notice, we will
            reach out to discuss your dispute with you.
          </strong>
        </li>
        <li>
          <strong>
            If we’re not able to resolve your dispute during our discussion, you
            will send us a written proposal for resolving your dispute.
          </strong>
        </li>
        <li>
          <strong>
            Within 15 business days after our receipt of your written proposal,
            we will let you know whether we agree to your proposal, or we will
            provide you with a counter-proposal.
          </strong>
        </li>
      </ol>
      <p>
        <strong>
          After Step 4, it’s up to you to decide whether you’d like to continue
          to negotiate with us to resolve your dispute, or whether you’d like to
          pursue a resolution through some other means.
        </strong>
      </p>
      <p>
        <strong>
          Throughout this process, both you and Plaid agree to negotiate in good
          faith and according to the terms of this section to resolve the
          dispute before resorting to litigation or some other form of dispute
          resolution procedure. All negotiations (including your notice, our
          discussions, and your and our proposals) pursuant to this section are
          confidential and treated as compromise and settlement negotiations for
          the purposes of federal and state rules of evidence and procedure.
        </strong>
      </p>
      <p>
        <strong>Notices</strong>.
      </p>
      <p>
        Plaid may provide notices or communications to you through the email
        associated with your Plaid profile, through my.plaid.com or through
        other reasonable methods. All notices, requests and other communications
        to Plaid under this Agreement must be in writing to Plaid Inc.,
        Attention: Legal, P.O. Box 7777 #35278, San Francisco, CA 94120-7775
        (with a courtesy copy to{' '}
        <Link href='mailto:legalnotices@plaid.com'>
          <a>legalnotices@plaid.com</a>
        </Link>{' '}
        ) and will be deemed given when delivered.
      </p>
      <p>
        <strong>Ending This Agreement</strong>.
      </p>
      <p>
        At any time in its discretion, Plaid may terminate or suspend this
        Agreement (or your use of the Platform) with or without notice and for
        any or no reason, including if Plaid suspects that you have violated
        this Agreement. Plaid will have no liability to you for any termination
        or suspension, nor will such action limit any other rights or remedies
        Plaid may have. Except for your right to use the Platform, this
        Agreement will survive any termination.
      </p>
      <p>
        <strong>About This Agreement</strong>.
      </p>
      <p>
        This Agreement may not be transferred or assigned by you without Plaid’s
        prior written consent. Plaid may assign or transfer this Agreement to
        its affiliates or in connection with a merger, sale, reorganization or
        other change of control. In addition, Plaid’s affiliates, contractors
        and service providers may exercise Plaid’s rights or fulfill its
        obligations under this Agreement. Waivers must be in writing and no
        waivers will be implied. If any provision of this Agreement is held by a
        court of competent jurisdiction to be unenforceable for any reason, the
        remaining provisions will remain unaffected and in full force and
        effect. This Agreement is the final, complete and exclusive agreement
        between you and us relating the subject matter of this Agreement and
        supersedes all prior or contemporaneous understandings and agreements
        relating to such subject matter, whether oral or written. In this
        Agreement, headings are for convenience only and the term “including”
        (and similar terms) will be construed without limitation.
      </p>
      <p>
        Plaid may modify this Agreement from time to time. Unless we specify
        otherwise, modifications take effect (and govern future use of the
        Platform, including my.plaid.com and your Plaid Profile) when we post
        the modified version. Plaid will use reasonable efforts to notify you of
        the modifications, and you may be required to agree to the modified
        version. If you do not agree to the modifications, your sole remedy is
        to cease using the Platform.
      </p>

      <hr className='section-divider' />
    </>
  );
};

export default USEndUserServicesAgreement;
